import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@shadcn-in-nx/ui/card";
import { Ticket, Loader2 } from "lucide-react";
import TicketListItem from "./TicketListItem";
import TicketPagination from "./TicketPagination";
import TicketFilters from "./TicketFilters";
import BulkActionsBar from "./BulkActionsBar";
import useTicketListKeyboard from "./useTicketListKeyboard";

export default function TicketList({
    tickets, isLoading, filters, selectedTickets, toggleSelectAll, toggleSelect,
    onBulkArchive, isArchiving, onNavigate, companies, limit, offset, total, onPageChange,
}) {
    const { t } = useTranslation();
    const { ticketViews, lastViewedId } = useMemo(() => {
        try {
            return {
                ticketViews: JSON.parse(localStorage.getItem("support_ticket_views") || "{}"),
                lastViewedId: localStorage.getItem("support_last_viewed_ticket"),
            };
        } catch { return { ticketViews: {}, lastViewedId: null }; }
    }, [tickets]);

    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const searchInputRef = useRef(null);

    useEffect(() => { setHighlightedIndex(0); }, [tickets.length]);
    useEffect(() => {
        const id = tickets[highlightedIndex]?.id;
        if (!id) return;
        document.getElementById(`support-ticket-row-${id}`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, [highlightedIndex, tickets]);

    const onOpen = useCallback((idx) => { const tk = tickets[idx]; if (tk) onNavigate(tk.id); }, [tickets, onNavigate]);
    const onSearch = useCallback(() => { const el = searchInputRef.current; if (el) { el.focus(); el.select?.(); } }, []);
    useTicketListKeyboard({ enabled: !isLoading && tickets.length > 0, length: tickets.length, highlightedIndex, setHighlightedIndex, onOpen, onSearch });

    return (
        <>
            <TicketFilters {...filters} searchInputRef={searchInputRef} />
            <BulkActionsBar tickets={tickets} selectedTickets={selectedTickets} toggleSelectAll={toggleSelectAll} onBulkArchive={onBulkArchive} isArchiving={isArchiving} />
            {isLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
            ) : tickets.length === 0 ? (
                <Card className="p-12 text-center">
                    <Ticket className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-semibold mb-2">{t("admin.support.noTickets", "No tickets found")}</h3>
                </Card>
            ) : (
                <div className="space-y-3">
                    {tickets.map((tk, i) => (
                        <TicketListItem key={tk.id} ticket={tk} isSelected={selectedTickets.has(tk.id)} onToggle={toggleSelect} onNavigate={onNavigate} companies={companies} isLastViewed={tk.id === lastViewedId} lastViewedAt={ticketViews[tk.id]} isHighlighted={i === highlightedIndex} />
                    ))}
                    <TicketPagination limit={limit} offset={offset} total={total} onPageChange={onPageChange} />
                </div>
            )}
        </>
    );
}
